import React from 'react';
import { INPUT_STEPS } from '../constants';

type FieldType = 'number' | 'currency' | 'text' | 'textarea';

interface EditableFieldProps {
    label: string;
    value: string | number;
    isEditing: boolean;
    hasError: boolean;
    fieldType: FieldType;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    ariaLabel: string;
}

/**
 * Generic editable field component for use in forms throughout the application.
 * 
 * Supports multiple field types (number, currency, text, textarea) with:
 * - View/Edit mode toggling
 * - Validation error styling
 * - Accessibility (ARIA attributes)
 * - Consistent formatting (e.g., currency with 2 decimals)
 * 
 * **Styling:** Uses `.editable-field-label` class (currently defined in payroll.style.scss)
 * When moving to common/, extract styles to a shared stylesheet.
 */

export const EditableField: React.FC<EditableFieldProps> = React.memo(({
    label,
    value,
    isEditing,
    hasError,
    fieldType,
    onChange,
    ariaLabel,
}) => {
    const displayValue = () => {
        if (fieldType === 'currency') {
            return typeof value === 'number' ? value.toFixed(2) : '';
        }
        return value ?? '';
    };

    const renderInput = () => {
        if (fieldType === 'textarea') {
            return (
                <textarea
                    className="form-control timesheet-textarea"
                    value={value || ''}
                    onChange={onChange}
                    aria-label={ariaLabel}
                    aria-invalid={hasError}
                />
            );
        }

        const inputType = fieldType === 'text' ? 'text' : 'number';
        const step = fieldType === 'currency' ? INPUT_STEPS.CURRENCY : INPUT_STEPS.DURATION;

        return (
            <input
                className="form-control"
                type={inputType}
                min={inputType === 'number' ? '0' : undefined}
                step={inputType === 'number' ? step : undefined}
                value={value ?? ''}
                onChange={onChange}
                aria-label={ariaLabel}
                aria-invalid={hasError}
            />
        );
    };

    return (
        <tr>
            <td className="editable-field-label">{label}</td>
            <td className={hasError && !isEditing ? 'has-error' : ''}>
                {isEditing ? renderInput() : displayValue()}
            </td>
        </tr>
    );
});

EditableField.displayName = 'EditableField';

